import React from 'react';
import { Assets, Colors, Dropdown, FontFamily, Input, PrimaryButton } from 'utils/imports.utils';
import { AccountSettingsTabs, ButtonSize, ButtonTypes } from 'utils/interface.utils';
import FileUpload from 'common_components/ui/file_upload/flie_upload.ui';
import { STRINGS } from 'constants/strings.constant';
import { AccountProfileTabProps } from 'interfaces/account_settings.interface';

const getInitials = (name?: string): string => {
  const words = (name ?? '').trim().split(/\s+/).filter(Boolean);
  return words.slice(0, 2).map((word) => word[0].toUpperCase()).join('') || '?';
};

// PrimaryButton paints its outlined border and text inline, so only an inline style can darken them.
const darkOutline = { border: `1.5px solid ${Colors.ink}`, color: Colors.ink, backgroundColor: Colors.white };

const AccountProfileTab: React.FC<AccountProfileTabProps> = ({
  profileData,
  photoSrc,
  defaultPhotoSrc,
  industry,
  type,
  showWorkFields = true,
  isSaving,
  onNameChange,
  onIndustryChange,
  onTypeChange,
  onPhotoUpload,
  onPhotoSizeError,
  onPhotoTypeError,
  onCancel,
  onSave,
}) => (
  <div className="account_settings_panel_profile">
    <h2 className="account_settings_panel_heading">{AccountSettingsTabs.PROFILE}</h2>
    <p className="account_settings_panel_subheading">{STRINGS.accountSettings.profileSubtitle}</p>
    <div className="account_settings_panel_profile_upload">
      <div className="account_settings_panel_profile_upload_photo_container">
        {photoSrc && photoSrc !== defaultPhotoSrc ? (
          <img src={photoSrc} alt="User Profile" />
        ) : (
          <span className="account_settings_panel_profile_upload_initials">{getInitials(profileData?.username)}</span>
        )}
      </div>
      <div className="account_settings_panel_profile_upload_button_container">
        <FileUpload
          fileTypes={['JPEG', 'JPG', 'PNG', 'WEBP', 'SVG', 'TIFF', 'AVIF']}
          maxFileSize={5 * 1024 * 1024}
          handleChange={onPhotoUpload}
          handleSizeError={onPhotoSizeError}
          handleTypeError={onPhotoTypeError}>
          <PrimaryButton
            onClick={() => {}}
            text="Upload photo"
            fontFamily={FontFamily.medium}
            lineHeight="20px"
            type={ButtonTypes.OUTLINED}
            size={ButtonSize.SMALL}
            left_icon={Assets.upload}
            iconSize={{ width: '16px', height: '16px' }}
            style={darkOutline}
          />
        </FileUpload>
        <div className="account_settings_panel_profile_upload_hint">{STRINGS.accountSettings.photoHint}</div>
      </div>
    </div>
    <div className="account_settings_panel_profile_form">
      <div className="account_settings_panel_profile_form_input_container">
        <Input
          value={profileData?.username ?? ''}
          label="Full name"
          type="text"
          placeholder="Enter your full name"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => onNameChange(e.target.value)}
        />
      </div>
      <div className="account_settings_panel_profile_form_input_container">
        <Input
          value={profileData?.email ?? ''}
          label="Email"
          type="email"
          placeholder="Enter your email id"
          disabled
          icon={Assets.lock}
          iconPosition="end"
          iconOnPress={() => {}}
          onChange={() => {}}
        />
        <div className="account_settings_panel_profile_form_help">{STRINGS.accountSettings.emailHelp}</div>
      </div>
      {showWorkFields && (<>
      <div className="account_settings_panel_profile_form_dropdown_container">
        <Dropdown
          label="Industry"
          data={[
            { label: 'IT', value: 'IT' },
            { label: 'Marketing', value: 'Marketing' },
            { label: 'Sales', value: 'Sales' },
            { label: 'HR', value: 'HR' },
          ]}
          value={industry}
          onChange={onIndustryChange}
        />
      </div>
      <div className="account_settings_panel_profile_form_dropdown_container">
        <Dropdown
          label="Type of work"
          data={[
            { label: 'Design', value: 'Design' },
            { label: 'Development', value: 'Development' },
          ]}
          value={type}
          onChange={onTypeChange}
        />
      </div>
      </>)}
    </div>
    <div className="account_settings_panel_profile_buttons_container">
      <div className="account_settings_panel_profile_buttons_cancel">
        <PrimaryButton
          onClick={onCancel}
          text="Cancel"
          type={ButtonTypes.OUTLINED}
          size={ButtonSize.MEDIUM}
          fontFamily={FontFamily.medium}
          lineHeight="20px"
          style={darkOutline}
        />
      </div>
      <div className="account_settings_panel_profile_buttons_save">
        <PrimaryButton
          onClick={onSave}
          text="Save changes"
          type={ButtonTypes.PRIMARY_GRADIENT}
          size={ButtonSize.MEDIUM}
          fontFamily={FontFamily.medium}
          lineHeight="20px"
          disabled={!profileData || isSaving}
          activity={isSaving}
        />
      </div>
    </div>
  </div>
);

export default AccountProfileTab;
